import type { Debt, PromoAlert, PromoAlertSeverity } from '@/types'
import { monthlyInterest } from './interestCalc'
import { parseLocalDate } from '@/lib/parseLocalDate'
import { isPaidOff } from './paidOff'

export function getMonthsRemaining(expiresAt: string, fromDate = new Date()): number {
  // parseLocalDate so a `YYYY-MM-DD` user entry isn't shifted into the
  // previous day (UTC-midnight vs local-midnight). Otherwise a promo
  // configured to expire May 15 reads as expiring late on May 14 in
  // US Pacific, and the engine flips to post-APR ~5h early.
  const expiry = parseLocalDate(expiresAt)
  const diffMs = expiry.getTime() - fromDate.getTime()
  return diffMs / (1000 * 60 * 60 * 24 * 30.44) // avg days per month
}

export function getPromoAlertSeverity(monthsRemaining: number): PromoAlertSeverity {
  if (monthsRemaining <= 0) return 'expired'
  if (monthsRemaining <= 3) return 'critical'
  if (monthsRemaining <= 6) return 'warning'
  return 'upcoming'
}

export function buildPromoAlerts(debts: Debt[], fromDate = new Date()): PromoAlert[] {
  const alerts: PromoAlert[] = []

  for (const d of debts) {
    // v0.8.57 — a cleared debt's expired promo isn't actionable.
    if (isPaidOff(d)) continue
    if (d.promo) {
      const promo = d.promo
      const monthsRemaining = getMonthsRemaining(promo.promoExpiresAt, fromDate)
      const severity = getPromoAlertSeverity(monthsRemaining)
      const currentMonthlyInterest = monthlyInterest(d.balance, promo.promoAPR)
      const postPromoMonthlyInterest = monthlyInterest(d.balance, promo.postPromoAPR)
      alerts.push({
        id: `promo-${d.id}-${promo.promoExpiresAt}`,
        kind: 'promo',
        debtId: d.id,
        debtName: d.name,
        currentAPR: promo.promoAPR,
        postPromoAPR: promo.postPromoAPR,
        expiresAt: promo.promoExpiresAt,
        monthsRemaining: Math.max(0, monthsRemaining),
        severity,
        estimatedMonthlyInterestIncrease: postPromoMonthlyInterest - currentMonthlyInterest,
      })
    }

    // Accepted BT offers get alerts too — same severity curve
    const bt = d.balanceTransferOffer
    if (bt && bt.acceptedAt) {
      const monthsRemaining = getMonthsRemaining(bt.offerValidUntil, fromDate)
      const severity = getPromoAlertSeverity(monthsRemaining)
      const currentMonthlyInterest = monthlyInterest(d.balance, bt.offerAPR)
      const postMonthlyInterest = monthlyInterest(d.balance, bt.postOfferAPR)
      alerts.push({
        id: `bt-${d.id}-${bt.offerValidUntil}`,
        kind: 'bt',
        debtId: d.id,
        debtName: d.name,
        currentAPR: bt.offerAPR,
        postPromoAPR: bt.postOfferAPR,
        expiresAt: bt.offerValidUntil,
        monthsRemaining: Math.max(0, monthsRemaining),
        severity,
        estimatedMonthlyInterestIncrease: postMonthlyInterest - currentMonthlyInterest,
      })
    }
  }

  return alerts.sort((a, b) => a.monthsRemaining - b.monthsRemaining)
}

/**
 * Returns the effective APR for a debt at a given simulation month.
 *
 * Priority order:
 * 1. Accepted BT offer (if within window) — its offerAPR wins
 * 2. Active promo (if within window) — its promoAPR wins
 * 3. Debt base APR
 *
 * If a promo or accepted BT has expired relative to the simulation month,
 * the post-expiry APR applies instead. If both exist, accepted BT takes
 * precedence because the balance has physically been moved to that rate.
 */
/**
 * v0.8.57 — `startDate` + n months, keeping the day but clamping to the
 * target month's length (Jan 31 + 1 → Feb 28, not Mar 3). The overflow made
 * a promo expiring early in a month switch to its post-promo rate a month
 * early in the simulation.
 */
export function addMonthsClamped(startDate: Date, months: number): Date {
  const y = startDate.getFullYear()
  const m = startDate.getMonth() + months
  const lastDay = new Date(y, m + 1, 0).getDate()
  return new Date(
    y,
    m,
    Math.min(startDate.getDate(), lastDay),
    startDate.getHours(),
    startDate.getMinutes(),
    startDate.getSeconds(),
    startDate.getMilliseconds()
  )
}

export function effectiveAPR(debt: Debt, simulationMonth: number, startDate: Date): number {
  const simDate = addMonthsClamped(startDate, simulationMonth)

  const bt = debt.balanceTransferOffer
  if (bt && bt.acceptedAt) {
    // parseLocalDate — see getMonthsRemaining for the rationale.
    const btExpiry = parseLocalDate(bt.offerValidUntil)
    return simDate >= btExpiry ? bt.postOfferAPR : bt.offerAPR
  }

  if (debt.promo) {
    const expiryDate = parseLocalDate(debt.promo.promoExpiresAt)
    return simDate >= expiryDate ? debt.promo.postPromoAPR : debt.promo.promoAPR
  }

  return debt.apr
}
